import { LoyaltyPromotionType } from 'generated/graphql-gateway';
import { useCart } from 'hooks/use-cart';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { getEvaluationResultMessage } from 'state/loyalty/hooks/utils/helpers';

import { IGetErrorMessagesProps } from './types';

export const useGetPaymentErrors = ({ cartPriceTooLow }: { cartPriceTooLow: boolean }) => {
  const incentiveEvaluationErrors = useAppSelector(state =>
    selectors.loyalty.selectIncentiveErrorsWithCode(
      state,
      IncentiveEvaluationErrorCodes.INVALID_PAYMENT_METHOD
    )
  );
  const { cartEntries } = useCart();

  // This method returns an array to handle other errors in the future
  // similar to `getErrorMessages`
  const getLoyaltyPaymentErrorMessages = () => {
    if (!incentiveEvaluationErrors.length) {
      return [];
    }

    return [
      {
        messageId: '' as TranslationKey,
        message: getEvaluationResultMessage({
          evaluationResult: incentiveEvaluationErrors[0],
          incentiveType: LoyaltyPromotionType.OFFER,
          cartEntries,
        }),
      },
    ];
  };

  const getErrorMessages = ({
    areDeliveryDetailsWithError,
    invalidAddressLine2,
    cartPriceTooLow: serverOrderCartPriceTooLow,
    checkoutPaymentMethodNeedRevaultError,
    invalidVehicleDescription,
    invalidOfferError,
    invalidPaymentMethodError,
    invalidReloadError,
    invalidTipAmountError,
    noDirectPayAndNoPrepaidMethodError,
    pricedAndAvailableError,
    storeIsClosing,
    unableToReloadCardError,
    unableToProcessOrderError,
  }: IGetErrorMessagesProps) => {
    const paymentErrors: Array<{
      hasError: boolean;
      message: string;
      messageId: TranslationKey;
    }> = [
      {
        message: t('invalidVehicleDescription'),
        messageId: 'invalidVehicleDescription',
        hasError: invalidVehicleDescription,
      },
      {
        message: t('invalidOfferError'),
        messageId: 'invalidOfferError',
        hasError: invalidOfferError,
      },
      {
        message: t('invalidPaymentMethodError'),
        messageId: 'invalidPaymentMethodError',
        hasError: invalidPaymentMethodError,
      },
      {
        message: t('invalidTipAmountError'),
        messageId: 'invalidTipAmountError',
        hasError: invalidTipAmountError,
      },
      {
        message: t('minimumPriceError'),
        messageId: 'minimumPriceError',
        hasError: !!cartPriceTooLow || !!serverOrderCartPriceTooLow,
      },
      {
        message: t('unableToReloadCardError'),
        messageId: 'unableToReloadCardError',
        hasError: unableToReloadCardError,
      },
      {
        message: t('pricedAndAvailableError'),
        messageId: 'pricedAndAvailableError',
        hasError: pricedAndAvailableError,
      },
      {
        message: t('areDeliveryDetailsValid'),
        messageId: 'areDeliveryDetailsValid',
        hasError: areDeliveryDetailsWithError,
      },
      {
        message: t('invalidAddressLine2'),
        messageId: 'invalidAddressLine2',
        hasError: invalidAddressLine2,
      },
      {
        message: t('noDirectPayAndNoPrepaidMethodError'),
        messageId: 'noDirectPayAndNoPrepaidMethodError',
        hasError: noDirectPayAndNoPrepaidMethodError,
      },
      {
        message: t('invalidReloadError'),
        messageId: 'invalidReloadError',
        hasError: invalidReloadError,
      },
      {
        message: t('checkoutPaymentMethodNeedRevaultError'),
        messageId: 'checkoutPaymentMethodNeedRevaultError',
        hasError: checkoutPaymentMethodNeedRevaultError,
      },
      {
        message: t('storeIsClosingError'),
        messageId: 'storeIsClosingError',
        hasError: storeIsClosing,
      },
      {
        message: t('couldntProcessOrder'),
        messageId: 'couldntProcessOrder',
        hasError: unableToProcessOrderError,
      },
    ];

    const paymentErrorMessageIds = paymentErrors.flatMap(item =>
      item.hasError ? [{ ...item }] : []
    );
    return paymentErrorMessageIds;
  };

  return {
    getErrorMessages,
    getLoyaltyPaymentErrorMessages,
  };
};
